import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import { createUser, StoreProps, updateUsers } from 'src/store/ducks/users'
import { profileOptions, ProfileValue } from 'src/constants/profile'
import { useAuth } from 'src/contexts/auth'

import { Button, Divider, SelectInput, Input, Loading } from 'src/components'
import { UsersProps } from 'src/store/ducks/users'
import { cpfMask, phoneMask } from 'src/utils'
import { cpf } from 'cpf-cnpj-validator'
import { toast } from 'react-toastify'
import { useStyles } from './styles'
import { API } from 'src/services/config'
import { Checkbox } from '@material-ui/core'

const schema = yup.object().shape({
  username: yup.string().required('Username é obrigatório').typeError('Campo obrigatório'),
  data: yup.object().shape({
    documento: yup.string().required('CPF é obrigatório').typeError('Campo obrigatório'),
    nome: yup.string().required('Nome é obrigatório').typeError('Campo obrigatório'),
    sobrenome: yup.string().required('Sobrenome é obrigatório').typeError('Campo obrigatório'),
    perfil: yup.string().required('Perfil do Usuário é obrigatório').typeError('Campo obrigatório'),
    idFazenda: yup.string().required('Propriedade é obrigatório').typeError('Campo obrigatório')
  })
})

interface NewRegisterProps {
  value: UsersProps
  onClose: React.MouseEventHandler | any
}

const RegisterUser: React.FC<NewRegisterProps> = ({ value, onClose }) => {
  const dispatch = useDispatch()
  const style = useStyles()

  const {
    setValue,
    handleSubmit,
    watch,
    clearErrors,
    formState: { errors }
  } = useForm<StoreProps>({ resolver: yupResolver(schema) })

  const { userFarm, username } = useAuth()

  const [isLoading, setIsLoading] = useState<any>({})
  const [property, setProperty] = useState<any>([{}])
  const [usernameAvailable, setUsernameAvailable] = useState(true)
  const [showPassword, setShowPassword] = useState(false)

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isIdentical, setIsIdentical] = useState(false)
  const [passwordIsValid, setPasswordIsValid] = useState(false)
  const [noChangePassword, setNoChangePassword] = useState(false)

  const onFocus = () => {
    const input = document.getElementById('registerUser__documento')
    input?.focus()
  }

  const onFocusUsername = () => {
    const input = document.getElementById('registerUser__username')
    input?.focus()
  }

  const onFocusPassword = () => {
    const input = document.getElementById('registerUser__password')
    input?.focus()
  }

  const onSubmit = handleSubmit((data) => {
    if (!value.username && password === '' && confirmPassword === '') {
      toast.error('Você não pode usar uma senha em branco')
      onFocusPassword()
      return
    }
    const defaultValue = {
      username: data.username || value.username,
      documento: data.data?.documento || value.data.documento,
      idFazenda: userFarm,
      nome: data.data?.nome || value.data.nome,
      sobrenome: data.data?.sobrenome || value.data.sobrenome,
      perfil: data.data?.perfil || value.data.perfil,
      telefone: data.data?.telefone ?? undefined,
      senha: data.data?.senha ?? value.data.senha
    }
    if (usernameAvailable) {
      if (confirmPassword === password || isIdentical) {
        if (passwordIsValid || noChangePassword) {
          // @ts-ignore
          if (cpf.isValid(data?.data?.documento)) {
            value.data ? dispatch(updateUsers({ ...defaultValue })) : dispatch(createUser({ ...defaultValue }))
            setTimeout(() => {
              onClose()
            }, 800)
          } else {
            toast.error('CPF inválido')
            onFocus()
            return
          }
        } else {
          toast.error('A senha deve conter pelo menos 8 caracteres, sendo: número, letra maiúscula e letra minúscula.')
          onFocusPassword()
          return
        }
      } else {
        toast.error('As senhas não correspondem.')
        onFocusPassword()
        return
      }
    } else {
      toast.error('Username indisponivel. Tente outro.')
      onFocusUsername()
      return
    }
  })

  async function getUsername(username) {
    if (value.username === undefined) {
      try {
        const response = await API.get(`${'/domain/Usuario/username'}/${username}`)
        if (response.data === '') {
          setUsernameAvailable(true)
        } else {
          setUsernameAvailable(false)
        }
      } catch (err: any) {
        setUsernameAvailable(true)
        return
      }
    }
  }

  async function getProperty(idFazenda) {
    try {
      const response = await API.get('/entity/Fazenda')
      const farmToShow = response.data.filter((item: any) => item.idFazenda === idFazenda)
      setProperty(farmToShow)
      setIsLoading(false)
    } catch (err: any) {
      toast.error('Não foi possivel carregar as propriedades, tente novamente.')
      setProperty([{}])
    }
  }

  async function changePassword() {
    if (password === '' || confirmPassword === '') {
      toast.error('As senhas não correspondem ou não foram preenchidas.')
      onFocusPassword()
      return
    }
    if (username === value.username) {
      toast.info('Usuário sem permissão para essa operação.')
      onFocusPassword()
      return
    }
    if (passwordIsValid) {
      if (confirmPassword === password) {
        try {
          await API.post('/mudarSenha', {
            username: watch('username'),
            senha: watch('data.senha'),
            adminUsername: username
          })
          toast.success('Senha alterada com sucesso!')
        } catch (err: any) {
          toast.error('Ocorreu um erro ao alterar a senha. Tente novamente.')
        }
      } else {
        toast.error('As senhas não correspondem.')
        onFocusPassword()
        return
      }
    } else {
      toast.info('A senha deve conter pelo menos 8 caracteres, sendo: número, letra maiúscula e letra minúscula.')
      onFocusPassword()
      return
    }
  }

  useEffect(() => {
    if (value?.username) {
      setValue('username', value?.username ?? '')
      setValue('data.nome', value?.data.nome ?? '')
      setValue('data.sobrenome', value?.data.sobrenome ?? undefined)
      setValue('data.telefone', phoneMask(value?.data.telefone) ?? undefined)
      setValue('data.documento', cpfMask(value?.data.documento) ?? undefined)
      setValue('data.perfil', value?.data.perfil ?? undefined)
      setValue('data.idFazenda', value?.data.idFazenda ?? undefined)
      setUsernameAvailable(true)
    }
  }, [])

  useEffect(() => {
    getProperty(userFarm)
  }, [])

  useEffect(() => {
    const regexValidation = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/
    if (value.username) {
      if (regexValidation.test(password) && regexValidation.test(confirmPassword)) {
        setPasswordIsValid(true)
      } else {
        setPasswordIsValid(false)
      }
    }

    if (!value.username) {
      if (regexValidation.test(password) && regexValidation.test(confirmPassword)) {
        setPasswordIsValid(true)
      } else {
        setPasswordIsValid(false)
      }
    }
  }, [password, confirmPassword])

  useEffect(() => {
    if (value.username && password === '' && confirmPassword === '') {
      setIsIdentical(true)
      setNoChangePassword(true)
    } else {
      setIsIdentical(false)
      setNoChangePassword(false)
    }
  }, [password, confirmPassword])

  // useEffect(() => {
  //   const password = document.getElementById('registerUser__password')
  //   const confirmation = document.getElementById('registerUser__confirm-password')

  //   console.log([password, confirmation])
  //   showPassword ?
  //     password.type = 'text' :
  //     type = 'password'
  // }, [showPassword])

  return (
    <>
      {isLoading && <Loading className={style.registerUserLoading} />}
      <div className={style.registerUserContainer}>
        <Input
          label='Username'
          autoComplete='off'
          placeholder='Username'
          disabled={!!value.username}
          id='registerUser__username'
          value={watch('username') || ''}
          error={errors?.username?.message}
          className={style.registerUserInput}
          onChange={(value: string) => {
            setValue('username', value)
            clearErrors('username')
            getUsername(watch('username'))
          }}
        />
        <Input
          label='Primeiro Nome'
          placeholder='Primeiro Nome'
          value={watch('data.nome') || ''}
          error={errors?.data?.nome?.message}
          className={style.registerUserInput}
          onChange={(value: string) => {
            setValue('data.nome', value)
            clearErrors('data.nome')
          }}
        />
        <Input
          label='Sobrenome'
          placeholder='Sobrenome'
          error={errors?.data?.sobrenome?.message}
          className={style.registerUserInput}
          value={watch('data.sobrenome') || ''}
          onChange={(value: string) => {
            setValue('data.sobrenome', value)
            clearErrors('data.sobrenome')
          }}
        />
        <Input
          label='CPF'
          maxLength={14}
          id='registerUser__documento'
          placeholder='000.000.000-00'
          error={errors?.data?.documento?.message}
          className={style.registerUserInput}
          value={watch('data.documento') || ''}
          onChange={(value: string) => {
            setValue('data.documento', cpf.format(value))
            clearErrors('data.documento')
          }}
        />
        <Input
          label='Telefone'
          onPressEnter={onSubmit}
          placeholder='(99) 99999-9999'
          className={style.registerUserInput}
          value={watch('data.telefone') || ''}
          onChange={(value: string) => setValue('data.telefone', phoneMask(value))}
        />
        <SelectInput
          options={property}
          label='Propriedade'
          selectBy='idFazenda'
          showBy={['descricao']}
          placeholder='SELECIONAR PROPRIEDADE'
          className={style.registerUserInput}
          error={errors?.data?.idFazenda?.message}
          value={watch('data.idFazenda') || ''}
          onChange={(item: any) => setValue('data.idFazenda', item.idFazenda)}
        />
        <SelectInput
          label='Perfil'
          selectBy='value'
          showBy={['value']}
          options={profileOptions}
          placeholder='Selecionar Perfil'
          value={watch('data.perfil') || ''}
          className={style.registerUserInput}
          error={errors?.data?.perfil?.message}
          onChange={(item: ProfileValue) => {
            setValue('data.perfil', item.value)
            clearErrors('data.perfil')
          }}
        />
        <Input
          label='Senha'
          maxLength={22}
          placeholder='Senha'
          id='registerUser__password'
          autoComplete='new-password'
          value={watch('data.senha') || ''}
          className={style.registerUserInput}
          type={showPassword ? 'text' : 'password'}
          onChange={(value: string) => {
            setValue('data.senha', value)
            setPassword(value)
            clearErrors('data.senha')
          }}
        />
        <Input
          maxLength={22}
          label='Confirmar Senha'
          autoComplete='new-password'
          value={confirmPassword || ''}
          placeholder='Confirme a Senha'
          className={style.registerUserInput}
          type={showPassword ? 'text' : 'password'}
          onChange={(value: string) => setConfirmPassword(value)}
        />
        <div
          className={style.registerUserShowPassword}
          style={{ display: confirmPassword && password ? 'flex' : 'none' }}
          onClick={() => setShowPassword(showPassword ? false : true)}
        >
          <Checkbox checked={true === showPassword} disabled />
          <label>Mostrar Senha</label>
        </div>
        <Divider />
        <div className={style.registerUserButtonContainer}>
          <Button
            style={{ display: value?.username ? 'block' : 'none' }}
            className={style.registerUserButtonPassword}
            onClick={changePassword}
          >
            Trocar Senha
          </Button>
          <Button className={style.registerUserButtonCancel} onClick={onClose}>
            Cancelar
          </Button>
          <Button className={style.registerUserButtonSave} onClick={onSubmit}>
            Salvar
          </Button>
        </div>
      </div>
    </>
  )
}

export default RegisterUser
